import { CoreImageView } from '@core/widgets';
import { useApp, useButton, useTriggerAction } from '@libs/hooks';
import { AssetsSvgMap, TBaseCalculatorData, TItem, TWidget } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { getHref } from '../../Image/imageUtils';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from '../../List/utils/ListConstant';
import { FlagIcon } from '../../UI/FlagIcon/FlagIcon';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { TextCommon } from '../../UI/Text/TextCommon';
import styles from '../Calculator.module.css';
import { ListCurrency } from '../ListCurrency';
import { ActionNameEnum, CurrencyDropdownEnum, TServerCalculationFunc } from '../utils/types';

type TCurrencyDropdownProps = {
    widget: TWidget<TBaseCalculatorData>;
    value: string;
    flagName?: string;
    name: CurrencyDropdownEnum;
    header: string;
    noFoundText?: string;
    currencyText?: string;
    buttonChangeTransferOptionLabel?: string;
    searchPlaceholder?: string;
    index: number;
    items: TItem[];
    isFrom?: boolean;
    isTopUp?: boolean;
    isDisabled: boolean;
    onServerCalculation: TServerCalculationFunc;
    searchEnabledItemsCount?: number;
};

const CurrencyDropdown = React.memo(
    ({
        widget,
        value,
        flagName,
        name,
        header,
        noFoundText,
        currencyText,
        index,
        items,
        isFrom,
        isTopUp,
        isDisabled,
        onServerCalculation,
        searchPlaceholder,
        buttonChangeTransferOptionLabel = '',
        searchEnabledItemsCount = SEARCH_ENABLED_ITEMS_COUNT_DEFAULT,
    }: TCurrencyDropdownProps) => {
        const { setPopup } = useApp();
        const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
        const { handleClickByActionName } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
        const isShowSearchFunctionality = items.length >= searchEnabledItemsCount;
        const button =
            !isTopUp && !isFrom
                ? [
                      <ButtonRowView
                          key="ChangeTransferOption"
                          label={buttonChangeTransferOptionLabel}
                          iconName="ic_arrows_exchange"
                          onClick={() => {
                              setPopup(null);
                              handleClickByActionName(ActionNameEnum.onChangeTransferOptions);
                          }}
                      />,
                  ]
                : [];

        const handleClick = () => {
            if (isDisabled) {
                return;
            }
            setPopup(
                <PopupComponent
                    fixedHeight={isShowSearchFunctionality}
                    withoutSeparator={isShowSearchFunctionality}
                    header={!isShowSearchFunctionality && header}
                    internalContent={
                        <ListCurrency
                            header={header}
                            noFoundText={noFoundText}
                            currencyText={currencyText}
                            isShowSearchFunctionality={isShowSearchFunctionality}
                            searchPlaceholder={searchPlaceholder}
                            index={index}
                            items={items}
                            handleClick={async (value) => {
                                setPopup(null);
                                await onServerCalculation(value, name, isFrom);
                            }}
                        />
                    }
                    externalContent={button}
                />,
            );
        };
        const classNames = clsx(styles.currencyDropdownBlock, isDisabled && styles.currencyDropdownBlockDisabled);
        return (
            <div className={classNames} onClick={handleClick} data-test={`calculator_currency_dropdown_${isFrom ?? ''}`}>
                <FlagIcon name={flagName ?? ''} />
                <TextCommon isWidget={false} content={value?.toUpperCase()} options={{ variant: 'main', textColor: 'black', margin: { left: 4 } }} />
                {!isDisabled && (
                    <CoreImageView
                        src={getHref(AssetsSvgMap.ic_chevron_down_small)}
                        defaultIcon={getHref(AssetsSvgMap.ic_x)}
                        styleProps={{
                            width: 16,
                            height: 16,
                            iconColor: `var(--color-black)`,
                        }}
                    />
                )}
            </div>
        );
    },
);

export { CurrencyDropdown };
